"use client";

import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { BaseFieldProps } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

type FieldPasswordProps = BaseFieldProps & React.ComponentProps<typeof Input> & {
    showVisibilityToggle?: boolean;
};

export function FieldPassword({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    showVisibilityToggle = false,
    ...rest
}: FieldPasswordProps) {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    return (
        <FormField
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
                    {showVisibilityToggle ? (
                        <div className="relative">
                            <FormControl>
                                <Input
                                    type={isPasswordVisible ? "text" : "password"}
                                    className="pr-10"
                                    {...field}
                                    {...rest}
                                />
                            </FormControl>
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label={isPasswordVisible ? "Şifreyi gizle" : "Şifreyi göster"}
                                aria-pressed={isPasswordVisible}
                                onClick={() => setIsPasswordVisible((visible) => !visible)}
                            >
                                {isPasswordVisible ? (
                                    <EyeOff className="size-4" aria-hidden="true" />
                                ) : (
                                    <Eye className="size-4" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    ) : (
                        <FormControl>
                            <Input type="password" {...field} {...rest} />
                        </FormControl>
                    )}
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
